import { LitElement, html, css } from 'lit'

import styleUrl from '../../css/styles.css?url'

class HedeliusScrollIndicator extends LitElement {
    static properties = {
        side: { type: String },
        activeNode: { type: Object },
    }

    static styles = css`
        :host {
            position: fixed;
            bottom: 0;
            width: 100%;
            z-index: 2005;
        }

        .indicator {
            height: 30px;
        }

        @keyframes bounce {
            0%,
            20%,
            50%,
            80%,
            100% {
                transform: translateY(0);
            }
            40% {
                transform: translateY(-15px);
            }
            60% {
                transform: translateY(-5px);
            }
        }

        @keyframes fadeIn {
            from {
                opacity: 0;
            }
            to {
                opacity: 1;
            }
        }

        @keyframes flyOut {
            0% {
                z-index: 100;
                opacity: 1;
                transform: translateY(0);
            }
            99% {
                z-index: 100;
                transform: translateY(200px);
                opacity: 0;
            }
            100% {
                z-index: -1;
            }
        }

        :host-context(.wrapper.open) .scroll-indicator-left.fadeIn,
        :host-context(.wrapper.open) .scroll-indicator-right.fadeIn {
            animation:
                fadeIn 2s,
                bounce 2s;
            z-index: 100;
        }

        .scroll-indicator-left.flyOut,
        .scroll-indicator-right.flyOut {
            animation: flyOut 1s ease-in;
        }

        .scroll-indicator-left.hide,
        .scroll-indicator-right.hide {
            opacity: 0;
            transition: opacity 0.5s 2s;
        }

        .icon-wrapper {
            box-shadow: 0 0 2px rgba(0, 0, 0, 0.3);
        }

        svg {
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
            border-radius: 9999px;
        }
    `

    constructor() {
        super()
        this.side = 'left'
        this._onScroll = this.handleScroll.bind(this)
        this._onMenuClick = this.handleMenuClick.bind(this)
    }

    firstUpdated() {
        document.addEventListener('menu-click', this._onMenuClick)
    }

    updated(changedProperties) {
        if (changedProperties.has('activeNode')) {
            setTimeout(() => {
                this.updateScrollContainer()
                this.animateScrollIndicator()
            }, 200)
        }
    }

    disconnectedCallback() {
        if (this.scrollContainer) {
            this.scrollContainer.removeEventListener('scroll', this._onScroll)
        }
        document.removeEventListener('menu-click', this._onMenuClick)
        super.disconnectedCallback()
    }

    updateScrollContainer() {
        if (this.scrollContainer) {
            this.scrollContainer.removeEventListener('scroll', this._onScroll)
        }
        const selector =
            this.side === 'left'
                ? 'hedelius-left-column:first-of-type'
                : 'hedelius-right-column:first-of-type'
        this.scrollContainer = this.parentElement.querySelector(selector)
        if (!this.scrollContainer) {
            console.error(`Element ${selector} not found.`)
            return
        }
        this.indicatorEl = this.renderRoot.querySelector('.indicator')
        if (this.scrollContainer && this.indicatorEl) {
            const width = this.scrollContainer.getBoundingClientRect().width
            this.indicatorEl.style.width = `${width}px`
            this.scrollContainer.addEventListener('scroll', this._onScroll)
        }
    }

    handleScroll() {
        this.animateScrollIndicator()
    }

    handleMenuClick() {
        setTimeout(() => {
            this.updateScrollContainer()
            this.animateScrollIndicator()
        }, 500)
    }

    animateScrollIndicator() {
        const tolerance = 70

        if (!this.scrollContainer || !this.indicatorEl) return
        requestAnimationFrame(() => {
            setTimeout(() => {
                if (
                    this.scrollContainer.scrollTop + this.scrollContainer.clientHeight >=
                    this.scrollContainer.scrollHeight - tolerance
                ) {
                    this.indicatorEl.classList.add('flyOut', 'hide')
                    this.indicatorEl.classList.remove('fadeIn')
                } else {
                    this.indicatorEl.classList.remove('flyOut', 'hide')
                    this.indicatorEl.classList.add('fadeIn')
                }
            }, 200)
        })
    }

    render() {
        return html`
            <link rel="stylesheet" href="${styleUrl}" />
            <div
                class="scroll-indicator-${this
                    .side} indicator fadeIn flex w-full items-center justify-center pb-8"
            >
                <div class="icon-wrapper h-8 w-8 rounded-full bg-white">
                    <svg
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 30 30"
                    >
                        <polyline points="21.84,12.65 14.86,19.62 8.16,12.92" />
                    </svg>
                </div>
            </div>
        `
    }
}

customElements.define('hedelius-scroll-indicator', HedeliusScrollIndicator)
